import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Loader2, Save, KeyRound, ShieldAlert, CheckCircle2, XCircle, BookOpen, PlugZap } from "lucide-react";
import IntegrationGuideDialog from "./IntegrationGuideDialog";

const TYPE_LABELS = {
  mercado_livre: "Mercado Livre",
  shopee: "Shopee",
  tiny_erp: "Tiny ERP",
  bling: "Bling ERP",
  amazon: "Amazon",
  magalu: "Magalu",
  tiktok_shop: "TikTok Shop",
  shein: "Shein",
};

const TYPE_DOCS = {
  mercado_livre: "developers.mercadolivre.com.br — crie um App e configure o Redirect URI.",
  shopee: "open.shopee.com — registre-se como Partner e gere Partner ID + Partner Key.",
  tiny_erp: "erp.olist.com/aplicativos_api → crie um App OAuth2 (redirect: https://SEU-DOMINIO/api/oauth/callback).",
  bling: "developer.bling.com.br → Meus Aplicativos → Novo App. Scopes: Pedidos-Vendas (rw) + Situações (r). Redirect: https://SEU-DOMINIO/api/oauth/callback.",
  amazon: "developer-docs.amazon.com/sp-api — registre LWA + AWS IAM.",
  magalu: "developers.magalu.com — crie APIKey no IDMagalu.",
  tiktok_shop: "partner.tiktokshop.com — registre seu App.",
  shein: "open.sheincorp.com — aprovação manual (B2B).",
};

export default function PlatformSettings() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({}); // type -> {fieldname: value}
  const [busy, setBusy] = useState(null);
  const [testing, setTesting] = useState(null);
  const [testResults, setTestResults] = useState({}); // type -> result dict
  const [guideOpen, setGuideOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/platform-settings");
      setList(data);
    } catch (e) {
      toast.error("Erro ao carregar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (type) => {
    setBusy(type);
    try {
      const credentials = { ...(list.find((l) => l.type === type)?.credentials || {}), ...(edits[type] || {}) };
      await api.put(`/platform-settings/${type}`, { type, credentials });
      toast.success(`${TYPE_LABELS[type]} salvo`);
      setEdits({ ...edits, [type]: {} });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao salvar");
    } finally {
      setBusy(null);
    }
  };

  const setField = (type, field, value) => {
    setEdits({ ...edits, [type]: { ...(edits[type] || {}), [field]: value } });
  };

  const testCredentials = async (type) => {
    setTesting(type);
    try {
      const { data } = await api.post(`/platform-settings/${type}/test`);
      setTestResults({ ...testResults, [type]: data });
      if (data.ok) {
        toast.success("Credenciais válidas — o Bling autenticou o App.");
      } else {
        toast.error(data.diagnosis || "Credenciais rejeitadas pelo Bling.");
      }
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Falha ao testar.";
      setTestResults({ ...testResults, [type]: { ok: false, diagnosis: msg } });
      toast.error(msg);
    } finally {
      setTesting(null);
    }
  };

  if (loading)
    return <div className="py-20 text-center"><Loader2 className="inline w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-4" data-testid="platform-settings-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Configuração do Vyntric</div>
          <h1 className="text-3xl font-black tracking-tighter">Credenciais de Plataforma</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-3xl">
            Configure aqui as credenciais dos seus <span className="font-bold">Apps</span> registrados em cada marketplace.
            Cada empresa cliente conecta a conta dela depois via OAuth, usando estas credenciais — você não precisa pedir
            token para o cliente final.
          </p>
        </div>
        <button
          data-testid="open-integration-guide"
          onClick={() => setGuideOpen(true)}
          className="px-4 py-2.5 text-xs font-bold uppercase tracking-wider bg-slate-900 hover:bg-[#002EA6] text-white flex items-center gap-2 self-start"
        >
          <BookOpen className="w-4 h-4" />
          Guia de Integração
        </button>
      </div>

      <IntegrationGuideDialog open={guideOpen} onClose={() => setGuideOpen(false)} />

      <div className="border border-amber-200 bg-amber-50 px-4 py-3 flex gap-3 items-start">
        <ShieldAlert className="w-5 h-5 text-amber-700 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-amber-900">
          Esta é a área mais sensível do sistema. As credenciais ficam <span className="font-bold">criptografadas em
          repouso</span> (Fernet AES-128 + HMAC) e nunca são exibidas em texto pleno após salvar — apenas os 4 últimos
          caracteres. Acesso restrito ao perfil <span className="font-mono">admin</span>.
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {list.map((s) => (
          <div key={s.type} className="border border-slate-200 bg-white" data-testid={`platform-${s.type}`}>
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="text-sm font-black tracking-tight">{TYPE_LABELS[s.type]}</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">{TYPE_DOCS[s.type]}</div>
              </div>
              <div className="flex items-center gap-1.5" style={{ color: s.configured ? "#059669" : "#9CA3AF" }}>
                {s.configured ? <CheckCircle2 className="w-4 h-4" /> : <KeyRound className="w-4 h-4" />}
                <span className="text-[10px] uppercase font-bold tracking-wider">
                  {s.configured ? "Configurado" : "Não configurado"}
                </span>
              </div>
            </div>
            <div className="p-4 grid grid-cols-1 gap-3">
              {s.fields.map((f) => (
                <label key={f} className="block">
                  <span className="block text-[10px] uppercase tracking-widest font-bold text-slate-700 mb-1 font-mono">
                    {f}{s.secret_fields.includes(f) && <span className="text-red-600 ml-1">(secreto)</span>}
                  </span>
                  <input
                    data-testid={`platform-${s.type}-${f}`}
                    type={s.secret_fields.includes(f) ? "password" : "text"}
                    placeholder={s.credentials?.[f] || ""}
                    value={edits[s.type]?.[f] ?? ""}
                    onChange={(e) => setField(s.type, f, e.target.value)}
                    className="w-full border border-slate-300 rounded-sm px-2 py-2 text-sm font-mono focus:outline-none focus:border-[#002EA6]"
                  />
                </label>
              ))}
              <div className="flex justify-end pt-1 gap-2 flex-wrap">
                {s.type === "bling" && s.configured && (
                  <button
                    data-testid={`platform-test-${s.type}`}
                    onClick={() => testCredentials(s.type)}
                    disabled={testing === s.type || busy === s.type}
                    className="px-4 py-2 text-xs font-bold uppercase tracking-wider border border-emerald-700 bg-emerald-50 hover:bg-emerald-100 text-emerald-900 flex items-center gap-2 disabled:opacity-60"
                    title="Envia uma sonda ao Bling e diz se o client_id/client_secret estão válidos"
                  >
                    {testing === s.type ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlugZap className="w-3.5 h-3.5" />}
                    Testar credenciais
                  </button>
                )}
                <button
                  data-testid={`platform-save-${s.type}`}
                  onClick={() => save(s.type)}
                  disabled={busy === s.type}
                  className="px-4 py-2 text-xs font-bold uppercase tracking-wider bg-[#002EA6] hover:bg-[#00207A] text-white flex items-center gap-2 disabled:opacity-60"
                >
                  {busy === s.type ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                  Salvar credenciais
                </button>
              </div>
              {testResults[s.type] && (
                <div
                  data-testid={`platform-test-result-${s.type}`}
                  className={`mt-2 border rounded-sm p-3 text-xs ${
                    testResults[s.type].ok
                      ? "border-emerald-300 bg-emerald-50 text-emerald-900"
                      : "border-red-300 bg-red-50 text-red-900"
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {testResults[s.type].ok ? (
                      <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5 text-emerald-600" />
                    ) : (
                      <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-red-600" />
                    )}
                    <div className="flex-1">
                      <div className="font-bold">
                        {testResults[s.type].ok ? "Credenciais válidas" : "Credenciais rejeitadas"}
                      </div>
                      <div className="mt-1">{testResults[s.type].diagnosis}</div>
                      {testResults[s.type].client_id_preview && (
                        <div className="mt-2 font-mono text-[10px] text-slate-600 space-y-0.5">
                          <div>client_id testado: <b>{testResults[s.type].client_id_preview}</b></div>
                          <div>redirect_uri configurado: <b>{testResults[s.type].redirect_uri_configured}</b></div>
                          {testResults[s.type].error_type && (
                            <div>resposta do Bling: <b>{testResults[s.type].error_type}</b></div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
